import { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { makeStyles, Typography, Box, CircularProgress } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import { ClientContext } from 'react-fetching-library';
import { useSelector } from 'react-redux';
import qs from 'qs';
import AccountListItem from '../../../../../Shared/AccountListItem/AccountListItem';
import { ReactComponent as NoteIcon } from '../../../../../../assets/icons/note.svg';

const PAGE_SIZE = 30;
const SCROLL_THRESHOLD = 50;

const useStyles = makeStyles(theme => ({
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  header: {
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    flexShrink: 0,
  },
  subtitle: {
    fontSize: 14,
    color: theme.colors?.grey || '#8E8E8E',
    marginTop: theme.spacing(0.5),
  },
  listContainer: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    paddingRight: theme.spacing(1),
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(0, 0, 0, 0.2)',
      borderRadius: 3,
      '&:hover': {
        background: 'rgba(0, 0, 0, 0.3)',
      },
    },
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4, 2),
    height: '100%',
  },
  emptyIcon: {
    color: theme.colors?.green || '#41C377',
    fontSize: 48,
    marginBottom: theme.spacing(2),
    opacity: 0.7,
  },
  emptyTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: theme.colors?.blue2 || '#122056',
    marginBottom: theme.spacing(1),
  },
  emptyText: {
    fontSize: 13,
    color: theme.colors?.grey || '#8E8E8E',
    textAlign: 'center',
    lineHeight: 1.5,
  },
  removeButton: {
    color: theme.colors?.grey || '#8E8E8E',
    '&:hover': {
      color: theme.colors?.darkError || '#dc1212',
      backgroundColor: 'rgba(220, 18, 18, 0.04)',
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
}));

const AllowedAccountsList = ({
  userUuid,
  accountUuid,
  includedAccountsUUIDs,
  excludedAccountsUUIDs,
  onRemoveAccount,
}) => {
  const classes = useStyles();
  const {
    en: { titles },
  } = useSelector(state => state.i18n);
  const client = useContext(ClientContext);

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const isLoadingRef = useRef(false);
  const continuationTokenRef = useRef(null);
  const prevPatchesRef = useRef(null);
  const patchesRef = useRef({ includedAccountsUUIDs, excludedAccountsUUIDs });
  const refetchTimerRef = useRef(null);

  // Keep patches ref in sync
  patchesRef.current = { includedAccountsUUIDs, excludedAccountsUUIDs };

  const buildEndpoint = useCallback(
    token => {
      const isAllowedBase64 = window.btoa('true');
      const baseEndpoint = `/allowedAccounts/users/${userUuid}/accounts/${accountUuid}`;

      if (token) {
        return `${baseEndpoint}${qs.stringify(
          { 'continuation-token': token },
          { addQueryPrefix: true }
        )}`;
      }

      return `${baseEndpoint}${qs.stringify(
        {
          fields: 'businessName,customIdentifier',
          q: `isAllowed:${isAllowedBase64}`,
          sort: 'businessName',
          pageSize: PAGE_SIZE,
        },
        { addQueryPrefix: true, arrayFormat: 'repeat' }
      )}`;
    },
    [userUuid, accountUuid]
  );

  const fetchData = useCallback(
    async ({ token = null, silent = false } = {}) => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;

      if (!silent) {
        setIsLoading(true);
      }

      try {
        const endpoint = buildEndpoint(token);
        const { payload, error } = await client.query({
          method: 'POST',
          endpoint,
          body: patchesRef.current,
        });

        if (!error && payload) {
          setItems(prev => (token ? [...prev, ...payload.items] : payload.items));
          setTotalCount(payload.totalCount);
          continuationTokenRef.current = payload.continuationToken || null;
        }
      } finally {
        isLoadingRef.current = false;
        setIsLoading(false);
        setIsInitialLoad(false);
      }
    },
    [client, buildEndpoint]
  );

  // Reload data when patches change
  useEffect(() => {
    const patchesKey = JSON.stringify({
      includedAccountsUUIDs,
      excludedAccountsUUIDs,
    });

    // First load -- show loader
    if (prevPatchesRef.current === null) {
      prevPatchesRef.current = patchesKey;
      setIsInitialLoad(true);
      fetchData();
      return;
    }

    // Patches changed -- optimistic update, debounced silent refetch
    if (prevPatchesRef.current !== patchesKey) {
      prevPatchesRef.current = patchesKey;

      // Optimistically remove excluded items from local list
      setItems(prev => prev.filter(item => !excludedAccountsUUIDs.includes(item.uuid)));
      setTotalCount(prev => Math.max(0, prev - 1));

      // Debounced silent background refetch -- waits for user to stop clicking
      clearTimeout(refetchTimerRef.current);
      continuationTokenRef.current = null;
      isLoadingRef.current = false;
      refetchTimerRef.current = setTimeout(() => {
        fetchData({ silent: true });
      }, 500);
    }
  }, [includedAccountsUUIDs, excludedAccountsUUIDs, fetchData]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => clearTimeout(refetchTimerRef.current);
  }, []);

  // Scroll-based infinite loading
  const handleScroll = useCallback(
    e => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const distanceToBottom = scrollHeight - scrollTop - clientHeight;

      if (
        distanceToBottom < SCROLL_THRESHOLD &&
        !isLoadingRef.current &&
        continuationTokenRef.current
      ) {
        fetchData({ token: continuationTokenRef.current });
      }
    },
    [fetchData]
  );

  const isUnlimitedAccess = !isInitialLoad && !isLoading && totalCount === 0;
  const accountLabel = totalCount === 1 ? 'account' : 'accounts';
  const subtitleText = isUnlimitedAccess ? '' : `${totalCount} ${accountLabel} selected`;

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography className={classes.subtitle}>{subtitleText}</Typography>
      </div>

      <div className={classes.listContainer} onScroll={handleScroll}>
        {isInitialLoad && isLoading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress size={24} />
          </Box>
        ) : isUnlimitedAccess ? (
          <div className={classes.emptyState}>
            <LockOpenIcon className={classes.emptyIcon} />
            <Typography className={classes.emptyTitle}>
              {titles.unlimitedAccessEnabled || 'Unlimited access is enabled'}
            </Typography>
            <Typography className={classes.emptyText}>
              {titles.unlimitedAccessDescription ||
                'This user has access to all accounts. Select specific accounts from the table to restrict access.'}
            </Typography>
          </div>
        ) : (
          <>
            {items.map(account => (
              <AccountListItem
                key={account.uuid}
                businessName={account.businessName}
                customIdentifier={account.customIdentifier}
                actionIcon={<CloseIcon fontSize="small" />}
                onAction={() => onRemoveAccount(account.uuid)}
                actionTooltip={titles.removeFromSelection || 'Remove from selection'}
                actionClassName={classes.removeButton}
              />
            ))}
            {isLoading && !isInitialLoad && (
              <Box className={classes.loadingContainer}>
                <CircularProgress size={20} />
              </Box>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AllowedAccountsList;
